import React, { FC, createContext, useContext, useEffect } from "react";
import CustomSelectInput from "../../../../shared/CustomSelectInput/CustomSelectInput";
import { useEndpoints } from "../../../../hooks/useEndpoints";
import {
  genericLoadOptions,
  getLabelFromOptions,
} from "../../../../lib/DurationPicker/utils";
import {
  GENDER_TEAM_OPTIONS,
  LEVELS_SPORT_OPTIONS,
  ROLES_SPORT_OPTIONS,
  USER_STATE_OPTIONS,
  USER_TYPE_OPTIONS,
} from "../../../../constants/config";
import { ITeam } from "../../../../models/Team";
import { GroupBase, OptionProps } from "react-select";
import classNames from "classnames";
import { CheckIcon, XIcon } from "@phosphor-icons/react";
import { TypeSpaceIcon } from "../../../../shared/Icons/Icons";
import { LEVELS_BY_SPORT, SPACE_TYPES } from "../../../../enums/Space";
import Typhography from "../../../../shared/Typhography/Typhography";
import style from "./SecondTab.module.scss";
import { IAddUserForm } from "../AddUserModal";
import { ISpace } from "../../../../models/Space";
import { USER_TYPES } from "../../../../enums/user";

interface ISecondTab {
  form: IAddUserForm;
  handleChange: any;
  isEditMode: boolean;
}

const ThemeContext = createContext(null as any);

export const CustomTeamOption: React.ComponentType<
  OptionProps<any, boolean, GroupBase<any>>
> = ({ innerProps, isDisabled, label, data }) => {
  const context = useContext(ThemeContext);
  if (isDisabled) return null;
  const { name, level, spaceId, _id } = data.value;
  const isSelected = context?.some?.((item: any) => item._id === _id);
  const { type } = spaceId;
  return (
    <div
      className={`container__custom__select__input__option ${classNames({
        not__selected: !isSelected,
      })}`}
      {...innerProps}
    >
      {isSelected && <CheckIcon />}
      <TypeSpaceIcon type={type as any} />
      <span className="label text-medium-16">
        {name}{" "}
        {`( ${getLabelFromOptions(
          LEVELS_SPORT_OPTIONS[type as keyof typeof LEVELS_BY_SPORT],
          level
        )} )`}
      </span>
    </div>
  );
};

const SecondTab: FC<ISecondTab> = ({ form, handleChange, isEditMode }) => {
  const { getSpaces, getTeams } = useEndpoints();

  const promiseOptions = genericLoadOptions(getSpaces, (group) => ({
    label: `${group.name} ( ${
      getLabelFromOptions(GENDER_TEAM_OPTIONS, group.gender) || ""
    } )`,
    value: group,
  }));
  const promiseOptions2 = genericLoadOptions(
    getTeams,
    (team: ITeam<{ type: keyof typeof LEVELS_SPORT_OPTIONS }>) => ({
      label: team.name,
      value: team,
    }),
    {
      spaceId:
        form.type === USER_TYPES.COACH
          ? (form.spaceId as any[])
              .map?.((item: any) => item?.value?._id)
              .filter(Boolean)
              .join(",")
          : (form.spaceId as any)?._id,
      populate: "spaceId.type",
      sort: "-isPreferred",
    }
  );

  useEffect(() => {
    if (form.type === USER_TYPES.ADMINISTRATOR) {
      handleChange("teams", []);
      handleChange("spaceId", undefined);
      handleChange("roles", undefined);
    }
  }, [form.type]);


  return (
    <div className="row g-3">
      <div className="col-sm-6 col-12">
        <CustomSelectInput
          onChange={(value) => {
            if (value === USER_TYPES.COACH) {
              handleChange("spaceId", []);
              handleChange("teams", []);
            } else if (value === USER_TYPES.ADMINISTRATOR) {
              handleChange("spaceId", null);
              handleChange("teams", []);
            } else {
              handleChange("spaceId", null);
              handleChange("teams", []);
            }
            handleChange("type", value);
          }}
          isDisabled={isEditMode}
          value={form.type}
          label="Tipo Account"
          options={USER_TYPE_OPTIONS}
        />
      </div>
      <div className="col-sm-6 col-12">
        <CustomSelectInput
          onChange={(value) => {
            handleChange("state", value);
          }}
          value={form.state}
          label="Stato"
          options={USER_STATE_OPTIONS}
        />
      </div>
      {form.type !== USER_TYPES.ADMINISTRATOR && (
        <>
          {form.type === USER_TYPES.ATHLETE && (
            <div className="col-sm-12 col-12">
              <CustomSelectInput
                label="Spazio *"
                onChange={({ value }) => {
                  handleChange(
                    "teams",
                    form.teams.filter(
                      (item: any) => value._id === item.spaceId?._id
                    )
                  );

                  handleChange("spaceId", value);
                  if (form.spaceId?.type !== (value as ISpace).type) {
                    handleChange("role", null);
                  }
                }}
                isMulti={false}
                value={
                  form.spaceId
                    ? { value: form.spaceId, label: form.spaceId.name }
                    : null
                }
                isOptionSelected={(item: any, h: any) => {
                  return h?.[0]?.value._id === item.value._id;
                }}
                placeholder="Cerca uno spazio..."
                loadOptions={promiseOptions}
              />
            </div>
          )}
          {form.type === USER_TYPES.COACH && (
            <div className="col-sm-12 col-12">
              <CustomSelectInput
                autoHeight
                label="Spazi *"
                onChange={(value) => {
                  const deduplicatedArray = Array.from(
                    new Map(
                      value.map((item: any) => [item.value.id, item])
                    ).values()
                  );
                  handleChange("spaceId", deduplicatedArray);
                  handleChange(
                    "teams",
                    form.teams.filter((item: any) =>
                      deduplicatedArray.some(
                        (space: any) => space.value._id === item.spaceId?._id
                      )
                    )
                  );
                }}
                isMulti={true}
                value={Array.isArray(form.spaceId) ? form.spaceId : []}
                isOptionSelected={(item: any, h: any) => {
                  return h.some((i: any) => i?.value?._id === item.value?._id);
                }}
                placeholder="Cerca uno o più spazio..."
                loadOptions={promiseOptions}
              />
            </div>
          )}
          {form.type === USER_TYPES.ATHLETE && (
            <div className="col-sm-12 col-12">
              <CustomSelectInput
                autoHeight
                onChange={(cc) => {
                  handleChange("roles", cc);
                }}
                value={form.roles}
                isDisabled={!form.spaceId}
                label="Ruoli"
                isMulti
                placeholder="Seleziona Ruoli..."
                options={
                  form.spaceId
                    ? ROLES_SPORT_OPTIONS?.[
                        (form.spaceId as any).type as SPACE_TYPES
                      ]
                    : []
                }
              />
            </div>
          )}
        </>
      )}
      {form.type !== USER_TYPES.ADMINISTRATOR && (
        <>
          <ThemeContext.Provider value={form.teams}>
            <CustomSelectInput
              label="Squadre"
              onChange={(value) => {
                if (!form.teams.some((team) => team._id === value.value._id))
                  handleChange("teams", [...form.teams, value.value]);
              }}
              isOptionSelected={(option, selectedValue) => {
                return false;
              }}
              components={{
                Option: CustomTeamOption,
              }}
              value={null}
              loadOptions={promiseOptions2}
            />
          </ThemeContext.Provider>
          <div className="d-flex flex-column gap-2">
            <Typhography size="bold-12" className="mb-1">
              {form.teams.length} team selezionati
            </Typhography>
            <div className={style.chipsContainer}>
              {form.teams.map((team) => {
                return (
                  <div className={style.chip} key={team._id}>
                    <TypeSpaceIcon type={team.spaceId.type} />
                    <Typhography size="medium-12">{team.name}</Typhography>
                    <Typhography size="medium-12" className="text-uppercase">
                      {getLabelFromOptions(
                        LEVELS_SPORT_OPTIONS[
                          team.spaceId.type as keyof typeof LEVELS_BY_SPORT
                        ],
                        team.level
                      )}
                    </Typhography>
                    <XIcon
                      className="cursor-pointer"
                      onClick={() => {
                        handleChange(
                          "teams",
                          form.teams.filter((item) => item._id !== team._id)
                        );
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SecondTab;
